import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PencilSimple, CircleNotch, CheckCircle, XCircle } from '@phosphor-icons/react'
import { userApi } from '@/api/users'
import type { OrgUserDetail, UpdateUserRequest } from '@/api/users'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'

interface UserProfileEditProps {
  user: OrgUserDetail
  orgId: string
  onUpdate: () => void
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

export function UserProfileEdit({ user, orgId, onUpdate }: UserProfileEditProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [editing, setEditing] = useState(false)

  // Form state
  const [name, setName] = useState(user.name)
  const [email, setEmail] = useState(user.email)
  const [phone, setPhone] = useState(user.phone ?? '')
  const [roleSlug, setRoleSlug] = useState(user.role_slug ?? '')

  // Email validation
  const [emailAvailable, setEmailAvailable] = useState<boolean | null>(null)
  const [emailChecking, setEmailChecking] = useState(false)
  const [emailError, setEmailError] = useState('')

  // Fetch roles
  const { data: roles } = useQuery({
    queryKey: ['roles', orgId],
    queryFn: () => userApi.listRoles(orgId),
    enabled: editing && !!orgId,
  })

  // Reset form when user prop changes or edit mode toggles
  useEffect(() => {
    setName(user.name)
    setEmail(user.email)
    setPhone(user.phone ?? '')
    setRoleSlug(user.role_slug ?? '')
    setEmailAvailable(null)
    setEmailChecking(false)
    setEmailError('')
  }, [user, editing])

  // Debounced email uniqueness check (only when email changed)
  useEffect(() => {
    if (!editing) return
    if (email === user.email) {
      setEmailAvailable(null)
      setEmailError('')
      return
    }
    if (!email || !isValidEmail(email)) {
      setEmailAvailable(null)
      setEmailError('')
      return
    }
    setEmailChecking(true)
    const timer = setTimeout(async () => {
      try {
        const result = await userApi.checkEmail(email, user.id)
        setEmailAvailable(result.available)
        setEmailError(result.available ? '' : 'Email already in use')
      } catch {
        setEmailAvailable(null)
        setEmailError('')
      }
      setEmailChecking(false)
    }, 300)
    return () => clearTimeout(timer)
  }, [email, editing, user.email, user.id])

  const updateMutation = useMutation({
    mutationFn: (data: UpdateUserRequest) => userApi.update(orgId, user.id, data),
    onSuccess: () => {
      toast({
        title: 'Profile updated',
        description: 'User profile has been saved.',
      })
      queryClient.invalidateQueries({ queryKey: ['user', orgId, user.id] })
      queryClient.invalidateQueries({ queryKey: ['users', orgId] })
      setEditing(false)
      onUpdate()
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update profile',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  function handleSave() {
    const changes: UpdateUserRequest = {}
    if (name.trim() !== user.name) changes.name = name.trim()
    if (email.trim() !== user.email) changes.email = email.trim()
    if ((phone.trim() || null) !== user.phone) changes.phone = phone.trim() || null
    if (roleSlug !== (user.role_slug ?? '')) changes.role_slug = roleSlug

    if (Object.keys(changes).length === 0) {
      setEditing(false)
      return
    }

    updateMutation.mutate(changes)
  }

  function handleCancel() {
    setEditing(false)
  }

  const canSave =
    name.trim() !== '' &&
    email.trim() !== '' &&
    isValidEmail(email) &&
    emailAvailable !== false &&
    !emailChecking &&
    !updateMutation.isPending

  if (!editing) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-lg">Profile</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setEditing(true)}
            className="text-muted-foreground"
          >
            <PencilSimple className="h-4 w-4 mr-1" />
            Edit
          </Button>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">Name</dt>
              <dd className="mt-1 text-sm font-medium">{user.name}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">Email</dt>
              <dd className="mt-1 text-sm font-medium">{user.email}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">Phone</dt>
              <dd className="mt-1 text-sm font-medium">{user.phone || 'Not set'}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">Role</dt>
              <dd className="mt-1 text-sm font-medium">{user.role_display_name || 'No role'}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-lg">Edit Profile</CardTitle>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCancel}
            disabled={updateMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!canSave}
          >
            {updateMutation.isPending && (
              <CircleNotch className="h-4 w-4 animate-spin mr-1" />
            )}
            Save
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Name */}
        <div className="space-y-1.5">
          <Label htmlFor="edit-name">Name</Label>
          <Input
            id="edit-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        {/* Email */}
        <div className="space-y-1.5">
          <Label htmlFor="edit-email">Email</Label>
          <div className="relative">
            <Input
              id="edit-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={
                emailError
                  ? 'border-destructive pr-9'
                  : emailAvailable === true
                    ? 'border-emerald-500 pr-9'
                    : 'pr-9'
              }
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {emailChecking && (
                <CircleNotch className="h-4 w-4 animate-spin text-muted-foreground" />
              )}
              {!emailChecking && emailAvailable === true && (
                <CheckCircle className="h-4 w-4 text-emerald-500" weight="fill" />
              )}
              {!emailChecking && emailAvailable === false && (
                <XCircle className="h-4 w-4 text-destructive" weight="fill" />
              )}
            </div>
          </div>
          {emailError && (
            <p className="text-xs text-destructive">{emailError}</p>
          )}
        </div>

        {/* Phone */}
        <div className="space-y-1.5">
          <Label htmlFor="edit-phone">Phone</Label>
          <Input
            id="edit-phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Not set"
          />
        </div>

        {/* Role */}
        <div className="space-y-1.5">
          <Label>Role</Label>
          {user.is_active ? (
            <Select value={roleSlug} onValueChange={setRoleSlug}>
              <SelectTrigger>
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {roles?.map((role) => (
                  <SelectItem key={role.slug} value={role.slug}>
                    {role.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <p className="text-sm text-muted-foreground">
              User is inactive. Reactivate to assign a role.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
